import { $t } from '@/plugins/fluent';
import { DateTime } from 'luxon';
import { type Component, computed, ComputedRef, Ref } from 'vue';
import { useLocationSelectionStore } from '../LocationSelection.store';
import {
    AllocationCount,
    countAllocationsByLocation,
    countOwnAndForeignAllocationsByLocation,
    OwnAndForeignAllocationCount,
    requireAllocationCount,
    sumAllocationCounts,
    sumOwnAndForeignAllocationCounts,
} from '../logic/allocationCounts';
import { countConflicts } from '../logic/conflictCounts';
import { getIcon, getIconTitle } from '../logic/icons';
import { Allocation, ConflictMarker, Section } from '../Timeline.types';
import { nestedLocationsErrorMessage, TimelineLocationData } from './useTimelineLocations';

type ConflictsCount = {
    resolved: number;
    unresolved: number;
    total: number;
};

/** A leaf location row rendered under a section, with the data needed for both the first column and the day row. */
type SectionLocationRowDataBase = TimelineLocationData & {
    sectionId: string;
    parentId: string;
    rowId: string;
    errorMessage: string | undefined;
    expandable: { isExpandable: false; conflictsCount: ConflictsCount };
    icon: { component: Component; title: string };
    // Consumed when building the matching day (layout) rows.
    allocations: Allocation[];
    conflictMarkers: Map<string, ConflictMarker>;
};

export type SectionLocationRowData = SectionLocationRowDataBase & { allocationCount: AllocationCount };

export type OwnAndForeignSectionLocationRowData = SectionLocationRowDataBase & {
    allocationCount: OwnAndForeignAllocationCount;
};

type SectionComputedData = {
    locations: SectionLocationRowDataBase[];
    conflictsCount: ConflictsCount;
    status: 'Has-Unresolved-Conflicts' | 'No-Conflicts';
};

/**
 * The state of a section's expand/collapse control. Exactly one of:
 * - `Enabled`: normal, toggleable.
 * - `Disabled`: the section holds the location that initiated selection mode; the triangle is shown but
 *   locked, with `explanation` as its tooltip.
 * - `Empty`: the section has no locations; a warning icon replaces the triangle, with `explanation` as
 *   its tooltip.
 */
export type SectionExpandableState =
    | { type: 'Enabled' }
    | { type: 'Disabled'; explanation: string }
    | { type: 'Empty'; explanation: string };

type SectionRowDataBase = SectionComputedData & {
    id: string;
    rowId: string;
    label: string;
    isExpanded: boolean;
    expandableState: SectionExpandableState;
    icon: { component: Component; title: string };
};

export type SectionRowData = Omit<SectionRowDataBase, 'locations'> & {
    locations: SectionLocationRowData[];
    allocationCount: AllocationCount;
};

export type OwnAndForeignSectionRowData = Omit<SectionRowDataBase, 'locations'> & {
    locations: OwnAndForeignSectionLocationRowData[];
    allocationCount: OwnAndForeignAllocationCount;
};

function hasSection(location: TimelineLocationData): location is TimelineLocationData & { sectionId: string } {
    return location.sectionId !== undefined;
}

function computeLocationRow(
    location: TimelineLocationData & { sectionId: string },
    locationAllocations: Allocation[],
    conflictMarkers: Map<string, ConflictMarker> | undefined,
): SectionLocationRowDataBase {
    const conflictCount = countConflicts(conflictMarkers);

    return {
        ...location,
        parentId: location.sectionId,
        rowId: `location-${location.id}`,
        errorMessage: nestedLocationsErrorMessage(location),
        expandable: {
            isExpandable: false as const,
            conflictsCount: {
                resolved: 0, // TODO: resolved/unresolved status will come from backend
                unresolved: conflictCount,
                total: conflictCount,
            },
        },
        icon: {
            component: getIcon(location.typeCategory, location.type),
            title: getIconTitle(location.typeCategory, location.type),
        },
        allocations: locationAllocations,
        conflictMarkers: conflictMarkers ?? new Map<string, ConflictMarker>(),
    };
}

type SectionRowsOptions = {
    baseSections: Ref<Section[]>;
    locations: Ref<TimelineLocationData[]>;
    allocationsByLocation: Ref<Map<string, Allocation[]>>;
    conflictMarkersByLocation: Ref<Map<string, Map<string, ConflictMarker>>>;
    startDateTime: Ref<DateTime | undefined>;
    endDateTime: Ref<DateTime | undefined>;
};

/**
 * Builds the sections-view rows: each configured section enriched with its leaf location rows and
 * rolled-up conflict counts.
 *
 * Performance: the expensive per-location computation (`sectionDataById`) only recomputes when the
 * allocations/locations/conflicts change — it does NOT depend on the sections' `isExpanded`
 * state, so toggling a section triggers only the cheap merge in `sections`.
 */
function useSectionRowsBase(options: SectionRowsOptions): {
    sections: ComputedRef<SectionRowDataBase[]>;
    containedAllocationsByLocationId: ComputedRef<Map<string, Allocation[]>>;
} {
    const { baseSections, locations, allocationsByLocation, conflictMarkersByLocation } = options;

    const locationSelectionStore = useLocationSelectionStore();

    // Expensive: pre-compute every location row and per-section rollup once.
    const sectionDataById = computed(() => {
        const allocsByLoc = allocationsByLocation.value;
        const conflictsByLoc = conflictMarkersByLocation.value;

        // Pre-compute all location rows once, grouped by sectionId
        const locationRowsBySectionId = new Map<string, SectionLocationRowDataBase[]>();
        for (const location of locations.value) {
            if (!hasSection(location)) {
                continue;
            }
            const row = computeLocationRow(
                location,
                allocsByLoc.get(location.id) ?? [],
                conflictsByLoc.get(location.id),
            );
            const existing = locationRowsBySectionId.get(location.sectionId) ?? [];
            existing.push(row);
            locationRowsBySectionId.set(location.sectionId, existing);
        }

        // Compute aggregations per section
        const result = new Map<string, SectionComputedData>();
        for (const [sectionId, sectionLocations] of locationRowsBySectionId) {
            let totalConflicts = 0;
            for (const loc of sectionLocations) {
                totalConflicts += loc.expandable.conflictsCount.total;
            }

            // TODO: resolved/unresolved status will come from backend
            const resolved = 0;
            const unresolved = totalConflicts;

            result.set(sectionId, {
                locations: sectionLocations,
                conflictsCount: { resolved, unresolved, total: totalConflicts },
                status:
                    totalConflicts > 0 && unresolved > 0
                        ? ('Has-Unresolved-Conflicts' as const)
                        : ('No-Conflicts' as const),
            });
        }
        return result;
    });

    const containedAllocationsByLocationId = computed(
        () =>
            new Map(
                Array.from(sectionDataById.value.values()).flatMap((section) =>
                    section.locations.map((location): [string, Allocation[]] => [location.id, location.allocations]),
                ),
            ),
    );

    // Cheap merge: combine static section metadata with the pre-computed data. Toggling `isExpanded`
    // only triggers this, not the expensive `sectionDataById` above.
    const sections = computed<SectionRowDataBase[]>(() =>
        baseSections.value.map((section) => {
            const data = sectionDataById.value.get(section.id) ?? {
                locations: [],
                conflictsCount: { resolved: 0, unresolved: 0, total: 0 },
                status: 'No-Conflicts' as const,
            };

            // An empty section can't hold the selection source, so `Empty` and `Disabled` are mutually
            // exclusive; check emptiness first.
            let expandableState: SectionExpandableState;
            if (data.locations.length === 0) {
                expandableState = { type: 'Empty', explanation: $t('error-section-empty') };
            } else {
                // Only lock while expanded: collapsing would hide the selection source, but expanding is
                // always safe, and locking a collapsed section makes the source unreachable.
                const lockExplanation = section.isExpanded
                    ? locationSelectionStore.getExpandableDisabledExplanation(section.id)
                    : null;
                expandableState = lockExplanation
                    ? { type: 'Disabled', explanation: lockExplanation }
                    : { type: 'Enabled' };
            }

            return {
                id: section.id,
                rowId: `section-${section.id}`,
                label: section.label,
                isExpanded: section.isExpanded,
                expandableState,
                icon: {
                    component: getIcon(section.typeCategory, section.type),
                    title: getIconTitle(section.typeCategory, section.type),
                },
                ...data,
            };
        }),
    );

    return { sections, containedAllocationsByLocationId };
}

/**
 * The sections-view rows, each location counting its allocations and each section summing those counts.
 */
export function useSectionRows(options: SectionRowsOptions): { sections: ComputedRef<SectionRowData[]> } {
    const base = useSectionRowsBase(options);

    // Independent of the expand state, so expanding or collapsing a section doesn't count again
    const allocationCountByLocationId = computed(() => {
        const start = options.startDateTime.value;
        const end = options.endDateTime.value;
        return start && end
            ? countAllocationsByLocation(base.containedAllocationsByLocationId.value, start, end)
            : undefined;
    });

    // No page renders the timeline before its range is known
    const sections = computed(() => {
        const allocationCounts = allocationCountByLocationId.value;
        if (!allocationCounts) {
            return [];
        }
        return base.sections.value.map((section) => {
            const locations = section.locations.map((location) => ({
                ...location,
                allocationCount: requireAllocationCount(allocationCounts, location.id),
            }));
            return {
                ...section,
                locations,
                allocationCount: sumAllocationCounts(locations.map((location) => location.allocationCount)),
            };
        });
    });

    return { sections };
}

/**
 * The sections-view rows, each location counting its own and its foreign allocations apart and each section
 * summing those counts.
 */
export function useOwnAndForeignSectionRows(options: SectionRowsOptions & { foreignAllocationIds: Ref<Set<string>> }): {
    sections: ComputedRef<OwnAndForeignSectionRowData[]>;
} {
    const base = useSectionRowsBase(options);

    // Independent of the expand state, so expanding or collapsing a section doesn't count again
    const allocationCountByLocationId = computed(() => {
        const start = options.startDateTime.value;
        const end = options.endDateTime.value;
        return start && end
            ? countOwnAndForeignAllocationsByLocation(
                  base.containedAllocationsByLocationId.value,
                  options.foreignAllocationIds.value,
                  start,
                  end,
              )
            : undefined;
    });

    // No page renders the timeline before its range is known
    const sections = computed(() => {
        const allocationCounts = allocationCountByLocationId.value;
        if (!allocationCounts) {
            return [];
        }
        return base.sections.value.map((section) => {
            const locations = section.locations.map((location) => ({
                ...location,
                allocationCount: requireAllocationCount(allocationCounts, location.id),
            }));
            return {
                ...section,
                locations,
                allocationCount: sumOwnAndForeignAllocationCounts(
                    locations.map((location) => location.allocationCount),
                ),
            };
        });
    });

    return { sections };
}
